'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import MaskedInput from '@/components/Form/MaskedInput'
import { ZIP_CODE_MASK } from '@/constants/masks'
import { CartShippingFormSchema, type ICartShippingForm } from '~/schemas/cart/cart'

interface IShippingFormProps {
    /** CEP do carrinho (só dígitos), para preencher o campo. */
    zipCode: string
    /** CEP do frete que vale hoje no carrinho (só dígitos), ou vazio: o campo volta a ele quando o novo CEP não cota. */
    lastValidZipCode?: string
    /** Resolve com `null` quando o CEP não cotou. */
    onCalculate: (zipCode: string) => Promise<unknown>
    /** Aviso fixo de CEP que não cotou. */
    error?: string | null
}

/**
 * "Calcular Frete" do carrinho. O campo com máscara ainda usa o `control` (o `MaskedInput` só tem essa entrada); o CEP
 * é validado pela regra única do endereço antes de chamar a API.
 */
const ShippingForm: React.FC<IShippingFormProps> = ({ zipCode, lastValidZipCode = '', onCalculate, error = null }) => {
    const { control, handleSubmit, setValue } = useForm<ICartShippingForm>({
        resolver: zodResolver(CartShippingFormSchema),
        defaultValues: { zipCode },
    })

    useEffect(() => {
        if (zipCode) setValue('zipCode', zipCode)
    }, [zipCode, setValue])

    return (
        <form
            className="w-full"
            onSubmit={handleSubmit(async ({ zipCode: value }) => {
                const result = await onCalculate(value)
                // O formulário só chama com CEP bem formado: `null` é CEP que a API não cotou. O campo volta ao CEP do frete
                // que continua valendo, para não mostrar um CEP e as opções de outro.
                if (result === null && lastValidZipCode) setValue('zipCode', lastValidZipCode)
            })}
        >
            <div className="flex flex-row items-center justify-between w-full">
                <label
                    htmlFor="CEP"
                    className="mr-4 font-extrabold md:text-xl max-lg:text-md text-destaques-hover font-sora"
                >
                    Calcular Frete
                </label>

                <MaskedInput
                    id="CEP"
                    name="zipCode"
                    placeholder="00000-000"
                    className="w-48 m-0 mr-1 text-lg text-secondary font-sora"
                    mask={ZIP_CODE_MASK}
                    control={control}
                    displayNestedButton
                    nestedButtonText="OK"
                />
            </div>
            {error && (
                <p role="alert" className="mt-2 text-sm text-red-600 font-sora">
                    {error}
                </p>
            )}
        </form>
    )
}

export default ShippingForm
